import { type AuthResponse, type DeskRosterDto, firstName, localTime } from '@hudhuria/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useDeferredValue, useEffect, useState } from 'react';
import { z } from 'zod';
import { DeskIcon } from '~/components/icons';
import { Keypad, PinDots } from '~/components/pin-pad';
import { Avatar, BottomSheet, EmptyState, RingLogo, SearchField, Spinner } from '~/components/ui';
import { ApiError, api, ok } from '~/lib/api';
import { completeSignIn } from '~/lib/auth';
import { toast } from '~/lib/toast';
import { useOnline } from '~/offline/online';
import { session, useSession } from '~/session/session';

export const Route = createFileRoute('/desk')({
  validateSearch: z.object({ switch: z.boolean().optional() }),
  beforeLoad: () => {
    if (!session.get().desk) throw redirect({ to: '/sign-in' });
  },
  component: DeskSignIn,
});

type Person = DeskRosterDto['staff'][number];

/**
 * Front-desk sign-in (devices an admin set up in Me → Front-desk mode): tap your name, enter your
 * PIN. Also where "Switch staff" goes at shift change.
 */
function DeskSignIn() {
  const navigate = useNavigate();
  const { switch: switching } = Route.useSearch();
  const { desk, token } = useSession();
  const online = useOnline();
  const [query, setQuery] = useState('');
  const q = useDeferredValue(query.trim().toLowerCase());
  const [who, setWho] = useState<Person | null>(null);

  const roster = useQuery({
    queryKey: ['desk-roster', desk?.id],
    queryFn: () => ok<DeskRosterDto>(api.auth.desk.$post({ json: { deskToken: desk?.token ?? '' } })),
    enabled: !!desk,
    // shown from cache straight away, but always re-checked: it's how a turned-off desk finds out
    staleTime: 0,
    refetchOnMount: 'always',
    retry: (n, e) => !(e instanceof ApiError && e.status === 401) && n < 2,
  });

  // An admin turned this desk off (here or from Team & roles on another device).
  const revoked = roster.error instanceof ApiError && roster.error.status === 401;
  useEffect(() => {
    if (!revoked) return;
    session.setDesk(null);
    toast('Front-desk mode was turned off for this device');
    void navigate({ to: token ? '/me' : '/sign-in', replace: true });
  }, [revoked, token, navigate]);

  if (!desk) return null;
  const people = (roster.data?.staff ?? []).filter((p) => !q || p.name.toLowerCase().includes(q));

  return (
    <div className="flex flex-1 animate-fade-in flex-col px-[22px] pt-safe-8 pb-safe">
      <div className="flex items-center justify-between">
        <RingLogo size={40} />
        <div className="flex items-center gap-1.5 rounded-[20px] bg-brand-50 px-[11px] py-1.5 text-[12px] font-semibold text-brand">
          <DeskIcon size={14} />
          {desk.centerName}
        </div>
      </div>
      <div className="mt-6 font-display text-[28px] font-bold tracking-[-0.02em]">
        {switching ? 'Who’s taking over?' : 'Who’s signing in?'}
      </div>
      <div className="mt-1.5 text-[14.5px] text-slate-500">Tap your name, then enter your 4-digit PIN.</div>

      {(roster.data?.staff.length ?? 0) > 8 && (
        <SearchField value={query} onChange={setQuery} placeholder="Find your name…" className="mt-4" />
      )}

      <div className="mt-4 -mx-1 flex-1 px-1 scroll-y">
        {roster.isPending && (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        )}
        {roster.isError && !revoked && (
          <EmptyState>
            {online ? roster.error.message : 'Signing in at the front desk needs a connection.'}
          </EmptyState>
        )}
        {roster.data && people.length === 0 && (
          <EmptyState>{q ? 'No one by that name.' : 'No staff at this center have a PIN yet.'}</EmptyState>
        )}
        <div className="grid grid-cols-2 gap-[10px]">
          {people.map((p) => (
            <button
              type="button"
              key={p.id}
              onClick={() => setWho(p)}
              className="flex cursor-pointer flex-col items-center gap-2 rounded-[18px] border border-line bg-white px-2 pt-4 pb-3 text-center active:bg-fill-soft"
            >
              <Avatar name={p.name} size={52} radius="full" fontSize={17} display />
              <div className="w-full">
                <div className="truncate text-[14px] font-semibold">{p.name}</div>
                <div className="mt-px truncate text-[11.5px] text-slate-400">{p.detail}</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={() => void navigate({ to: switching ? '/me' : '/sign-in', replace: !!switching })}
        className="mt-3 cursor-pointer border-none bg-transparent py-2 text-[13.5px] font-semibold text-slate-500"
      >
        {switching ? 'Cancel' : 'Use email & password instead'}
      </button>

      {who && <PinSheet desk={desk} person={who} onClose={() => setWho(null)} />}
    </div>
  );
}

function PinSheet({
  desk,
  person,
  onClose,
}: {
  desk: { token: string; centerId: string };
  person: Person;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [pin, setPin] = useState('');
  const [prompt, setPrompt] = useState('Enter your 4-digit PIN');
  const [shake, setShake] = useState(0);
  const [done, setDone] = useState(false);

  const signIn = useMutation({
    mutationFn: (p: string) =>
      ok<AuthResponse>(api.auth.pin.$post({ json: { deskToken: desk.token, staffId: person.id, pin: p } })),
    onSuccess: (res) => {
      setDone(true);
      setPrompt('Verified — starting your shift');
      setTimeout(() => {
        completeSignIn(res);
        session.setCenter(desk.centerId); // a desk belongs to one center
        toast(
          `Signed in as ${firstName(res.me.staff.name)} · shift started ${localTime(new Date(), res.me.institution.timezone)}`,
        );
        void navigate({ to: '/hub', replace: true });
      }, 600);
    },
    onError: (e) => {
      setPin('');
      setShake((n) => n + 1);
      setPrompt(e.message);
    },
  });

  const press = (d: string) => {
    if (done || signIn.isPending) return;
    const next = (pin + d).slice(0, 4);
    setPin(next);
    if (next.length === 4) signIn.mutate(next);
  };

  return (
    <BottomSheet onClose={onClose}>
      <div className="flex items-center gap-[13px]">
        <Avatar name={person.name} size={52} radius="full" color="#0369a1" fontSize={17} display />
        <div className="min-w-0 flex-1">
          <div className="truncate font-display text-[19px] font-bold">{person.name}</div>
          <div className="mt-px truncate text-[12.5px] text-slate-500">{person.detail}</div>
        </div>
      </div>
      <div className="mt-[18px] text-center text-[13.5px] font-semibold text-slate-700" aria-live="polite">
        {prompt}
      </div>
      <PinDots key={shake} length={pin.length} ok={done} shake={shake > 0} />
      <Keypad onDigit={press} onDelete={() => setPin((p) => p.slice(0, -1))} disabled={done} />
      <button
        type="button"
        onClick={onClose}
        className="mt-3 w-full cursor-pointer border-none bg-transparent py-2 text-[13px] font-semibold text-slate-500"
      >
        Not you? Pick another name
      </button>
    </BottomSheet>
  );
}
